<template>
  <div id="directory-information-report">

    <!-- Member Document Dialog -->
    <v-dialog v-model="memberDocumentPrompt" persistent scrollable>
      <member-document-list v-if="memberDocumentPrompt" :record="selectedData" :pageDescription="memberDocumentTitle"
        @hideDialog="hideMemberDocumentPrompt($event)"></member-document-list>
    </v-dialog>

    <!-- Preview Record Dialog -->
    <v-dialog v-model="previewRecordPrompt" max-width="75%" persistent>
      <directory-informatiom-preview :previewRecordPrompt="previewRecordPrompt"
        @hidePreviewRecordPrompt="hidePreviewRecordPrompt" :recordData="selectedData"
        v-if="previewRecordPrompt"></directory-informatiom-preview>
    </v-dialog>

    <!-- Assign Member Dialog -->
    <v-dialog v-model="assignMemberPrompt" max-width="75%" persistent>
      <assign-member PageTitle="Assign Member" :assignMemberPrompt="assignMemberPrompt"
        @hideAssignMemberPrompt="hideAssignMemberPrompt" :recordData="selectedData"
        v-if="assignMemberPrompt"></assign-member>
    </v-dialog>

    <!-- Main Card -->
    <v-card class="elevation-3 rounded-lg">
      <!-- Header -->
      <v-card-title class="primary darken-1 white--text py-4">
        <div class="d-flex flex-column">
          <h2 class="mb-1 font-weight-bold">{{ PageTitle }}</h2>
          <div class="text-subtitle-1 font-weight-regular">
            {{ PageDescription }}
          </div>
        </div>
      </v-card-title>

      <v-card-text class="pt-6">
        <!-- Required Field Note -->
        <v-alert dense text color="grey darken-1" class="mb-4" icon="mdi-information-outline">
          <span class="text-caption">Fields marked with <span class="red--text">*</span> are
            required</span>
        </v-alert>

        <!-- Action Buttons -->
        <div class="d-flex flex-wrap mb-4 justify-end">
          <v-btn class="mr-2 mb-2" color="primary" @click.prevent="refreshPageData" rounded elevation="2">
            <v-icon left>mdi-refresh</v-icon>
            Refresh
          </v-btn>

          <v-btn v-if="RoleId == 2" class="mr-2 mb-2" color="error" @click.prevent="regenerateRecords"
            :loading="SubmitFlag" rounded elevation="2">
            <v-icon left>mdi-reload</v-icon>
            Regenerate
          </v-btn>
          <v-btn class="mr-2 mb-2" color="success" @click.prevent="assignMemberPrompt = true" rounded elevation="2">
            <v-icon left>mdi-plus</v-icon>
            Assign Member
          </v-btn>
        </div>

        <!-- Loading Spinner -->
        <div class="d-flex justify-center my-6" v-if="LoadingFlag">
          <v-progress-circular indeterminate color="primary" size="60" width="6">
            Loading
          </v-progress-circular>
        </div>

        <!-- Search Form -->
        <v-card outlined class="pa-4 mb-6">
          <v-form ref="form2" v-model="valid1" lazy-validation v-on:submit.prevent="searchForm">
            <v-row>
              <v-col cols="12">
                <div class="text-h6 primary--text mb-2">Search Filters</div>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <v-autocomplete v-model="JciYearCode" :rules="JciYearCodeRules" :items="JciYearCodeOptions"
                  :loading="JciYearCodeOptionsLoading" label="Year" dense filled clearable required hide-details="auto"
                  prepend-inner-icon="mdi-calendar">
                  <template v-slot:prepend-item>
                    <v-divider class="mb-2"></v-divider>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <v-autocomplete v-model="ZoneCode" :rules="ZoneCodeRules" :items="ZoneCodeOptions"
                  :loading="ZoneCodeOptionsLoading" label="Zone (optional)" dense filled clearable hide-details="auto"
                  prepend-inner-icon="mdi-map-marker-radius">
                </v-autocomplete>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <v-autocomplete v-model="LomCode" :rules="LomCodeRules" :items="LomCodeOptions"
                  :loading="LomCodeOptionsLoading" label="LO (optional)" dense filled clearable hide-details="auto"
                  prepend-inner-icon="mdi-office-building">
                </v-autocomplete>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <v-autocomplete v-model="DesignationCode" :rules="DesignationCodeRules" :items="DesignationCodeOptions"
                  :loading="DesignationCodeOptionsLoading" label="Designation (optional)" dense filled clearable
                  hide-details="auto" prepend-inner-icon="mdi-account-tie" multiple chips deletable-chips>
                </v-autocomplete>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <v-select v-model="ApprovalStatus" :items="ApprovalStatusOptions" :rules="ApprovalStatusRules"
                  label="Approval Status (optional)" dense filled clearable hide-details="auto"
                  prepend-inner-icon="mdi-check-decagram">
                </v-select>
              </v-col>

              <v-col cols="12" sm="6" md="4" lg="3">
                <v-select v-model="MigrationStatus" :items="MigrationStatusOptions" :rules="MigrationStatusRules"
                  label="Migration Status (optional)" dense filled clearable hide-details="auto"
                  prepend-inner-icon="mdi-arrow-decision">
                </v-select>
              </v-col>
            </v-row>

            <!-- Search Buttons -->
            <v-card-actions class="pt-4 justify-center">
              <v-btn :disabled="!valid1" @click.prevent="searchForm" :loading="SearchFlag" color="primary" large
                class="px-6" rounded elevation="2">
                <v-icon left>mdi-magnify</v-icon>
                Search
              </v-btn>

              <v-btn @click.prevent="resetForm" color="grey darken-1" large class="px-6 ml-4" outlined rounded>
                <v-icon left>mdi-refresh</v-icon>
                Reset
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>

        <!-- Results Section -->
        <v-card v-if="tableData1.length > 0" outlined class="mb-6">
          <v-card-title class="primary lighten-1 white--text">
            <v-icon left color="white">mdi-format-list-bulleted</v-icon>
            Search Results
            <v-chip class="ml-2" color="white" text-color="primary">
              {{ tableData1.length }} records found
            </v-chip>
            <v-spacer></v-spacer>

            <!-- Download Buttons -->
            <v-btn v-if="DownloadFlag" color="white" @click.prevent="
              generateExcel(
                tableData1,
                ExcelFields,
                ExcelFileName,
                'My Worksheet'
              )
              " class="mr-2" small>
              <v-icon left>mdi-file-excel</v-icon>
              Export to Excel
            </v-btn>

            <v-btn small color="white" v-if="ExcelDownloadFlag == true && ExcelFileFlag == false"
              @click.prevent="generateReportExcel" :loading="ExcelLoadingFlag" class="mr-2">
              <v-icon left>mdi-file-document-outline</v-icon>
              Generate Report
            </v-btn>

            <v-btn small color="success" v-if="ExcelFileName != '' && ExcelFileFlag == true" :href="ExcelFileName"
              target="_blank" dark>
              <v-icon left>mdi-download</v-icon>
              Download Report
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-0">
            <!-- Search Input -->
            <v-card-text class="py-2">
              <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Search within results" filled dense
                hide-details clearable></v-text-field>
            </v-card-text>

            <!-- Data Table -->
            <v-data-table class="elevation-0" loading-text="Loading... Please wait" v-model="selected" :search="search"
              :loading="TableLoadingFlag" :item-key="tableOptions1.ItemKey" :headers="tableColumns1" :items="tableData1"
              :items-per-page="tableOptions1.ItemsPerPage" :footer-props="tableOptions1.FooterProps" multi-sort
              fixed-header>
              <template v-slot:[`item.YearwiseOfficeHeldStatusTxt`]="{ item }">
                <v-chip :color="getActiveStatusColor(item.YearwiseOfficeHeldStatusTxt)
                  " small label dark>{{ item.YearwiseOfficeHeldStatusTxt }}</v-chip>
              </template>

              <template v-slot:[`item.DOB`]="{ item }">
                <v-chip :color="item.DOBColor" small label dark>{{
                  item.DOB
                  }}</v-chip>
              </template>
              <template v-slot:[`item.ProfilePic`]="{ item }">
                <v-avatar size="50" color="grey lighten-3">
                  <v-img :src="item.ProfilePic" :alt="item.MemberName"></v-img>
                </v-avatar>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex align-center">
                  <v-tooltip bottom v-if="item.MigrationFlag">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="warning" @click="updateUserRole(item)" v-bind="attrs" v-on="on" class="mr-1">
                        <v-icon>mdi-sync-alert</v-icon>
                      </v-btn>
                    </template>
                    <span>Migration Required: Current Role and Designation Role mismatch</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="primary" @click="previewData(item)" v-bind="attrs" v-on="on"
                        class="mr-1">
                        <v-icon>mdi-file-search</v-icon>
                      </v-btn>
                    </template>
                    <span>Preview Details</span>
                  </v-tooltip>

                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="error" @click="deleteAlert(item)" v-bind="attrs" v-on="on">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete Record</span>
                  </v-tooltip>
                </div>
              </template>

              <template v-slot:no-data>
                <v-alert outlined type="info" class="ma-4" icon="mdi-information">
                  No data available
                </v-alert>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import companyConfig from "@/company_config.json";
import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME,
} from "@/core/services/store/htmlclass.module.js";
import LomPresidentAssign from "@/view/pages/erp/lom-president/LomPresidentAssign.vue";
import LomDocumnets from "@/view/pages/erp/lom/LomDocumnets.vue";
import MemberDocumentList from "@/view/pages/members/MemberDocumentList.vue";
import AssignMember from "@/view/pages/erp/reports/AssignMember.vue";
import Swal from "sweetalert2";
import DirectoryInformatiomPreview from "./DirectoryInformatiomPreview.vue";
export default {
  mixins: [common],
  components: {
    LomPresidentAssign,
    LomDocumnets,
    MemberDocumentList,
    AssignMember,
    DirectoryInformatiomPreview,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      SpeedDialFlag: false,
      TableLoadingFlag: false,

      memberDocumentPrompt: false,
      memberDocumentTitle: "",

      ExcelFileFlag: false,

      DownloadFlag: false,
      ExcelFileName: "",
      ExcelFields: {},

      rows: [],

      Modules: {},

      tableData1: [],
      tableColumns1: [],
      tableOptions1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,

      excel_fields: {
        "LO Id": "LomId",
        "Zone Id": "ZoneId",
        "LO Name": "LomName",
        "President Name": "LomPresidentFullName",
        "LO NAME": "LomName",
        "Mobile No": "LomPresidentMobileNo",
        "Email Id": "LomPresidentEmailid",
      },

      recordToEdit: "",
      record: {},
      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,
      editPageDescription: "Assign LO President for JCI India",

      selectedData: {},
      assignMemberPrompt: false,
      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      zone: {},
      lom: {},

      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCode: "",
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      AreaCodeRules: [(v) => !!v || "Area is required"],
      AreaCode: "",
      AreaCodeOptions: [],
      AreaCodeOptionsLoading: false,

      ZoneCodeRules: [],
      ZoneCode: "",
      ZoneCodeOptions: [],
      ZoneCodeOptionsLoading: false,

      RegionCodeRules: [],
      RegionCode: "",
      RegionCodeOptions: [],
      RegionCodeOptionsLoading: false,

      LomCodeRules: [],
      LomCode: "",
      LomCodeOptions: [],
      LomCodeOptionsLoading: false,

      DesignationCodeRules: [],
      DesignationCode: [],
      DesignationCodeOptions: [],
      DesignationCodeOptionsLoading: false,

      ApprovalStatusRules: [],
      ApprovalStatus: "",
      ApprovalStatusOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Approved" },
        { value: 2, text: "Pending" },
        { value: 3, text: "Rejected" },
        { value: 4, text: "Cancelled" },
      ],

      MigrationStatusRules: [],
      MigrationStatus: "",
      MigrationStatusOptions: [
        { value: "", text: "--Select--" },
        { value: 0, text: "Not migrated" },
        { value: 1, text: "Migrated" },
      ],

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      RoleId: "",
    };
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.CurrentYearId = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var RoleId = this.$session.get("RoleId");
        RoleId = RoleId == (null || undefined) ? "" : RoleId;
        console.log({ RoleId });
        // RoleId = 4;
        this.RoleId = RoleId;

        this.JciYearCodeOptionsLoading = true;
        this.getYearCodeOptions();
        this.getDesignationCodeOptions();
      }
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.LoadingFlag = false;
      this.JciYearCodeOptionsLoading = false;
    },
    JciYearCode: function () {
      console.log("watch JciYearCode");
      // this.getAreaCodeOptions();
      this.ZoneCode = "";
      this.getZoneCodeOptions();
    },
    AreaCodeOptions: function () {
      console.log("watch AreaCodeOptions");
      this.LoadingFlag = false;
      this.AreaCodeOptionsLoading = false;
    },
    AreaCode: function () {
      console.log("watch AreaCode");
      // this.getZoneCodeOptions();
    },
    ZoneCodeOptions: function () {
      console.log("watch ZoneCodeOptions");
      this.LoadingFlag = false;
      this.ZoneCodeOptionsLoading = false;
    },
    ZoneCode: function () {
      console.log("watch ZoneCode");
      // this.getRegionCodeOptions();
      this.LomCode = "";
      this.getLomCodeOptions();
    },
    RegionCodeOptions: function () {
      console.log("watch RegionCodeOptions");
      this.LoadingFlag = false;
      this.RegionCodeOptionsLoading = false;
    },
    RegionCode: function () {
      console.log("watch RegionCode");
      // this.getLomCodeOptions();
    },
    LomCodeOptions: function () {
      console.log("watch LomCodeOptions");
      this.LoadingFlag = false;
      this.LomCodeOptionsLoading = false;
    },
    DesignationCodeOptions: function () {
      console.log("watch DesignationCodeOptions");
      this.LoadingFlag = false;
      this.DesignationCodeOptionsLoading = false;
    },
  },
  methods: {
    hideMemberDocumentPrompt(flag) {
      console.log("hideMemberDocumentPrompt called");
      console.log({ flag });
      this.memberDocumentPrompt = false;
      if (flag) {
        // this.member.document_flag = true;
      }
    },
    addRecord() {
      console.log("addRecord called");
      var url = "/lom/create";
      var params = {};
      this.pageRedirect(params, url);
    },
    resetForm() {
      this.$refs.form2.reset();
      this.tableData1 = [];
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      this.searchForm();
    },
    hideEditRecordPrompt() {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      this.searchForm();
    },
    memberDocumentData(tr) {
      console.log("memberDocumentData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedData = tr;
        this.memberDocumentTitle="Documents for " + tr.MemberName;
        this.memberDocumentPrompt = true;
      } else {
        var message = "Kindly select one member.";
        this.toast("error", message);
      }
    },
    hidePreviewRecordPrompt(flag) {
      console.log("hidePreviewRecordPrompt called");
      console.log({ flag });
      this.previewRecordPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },
    hideAssignMemberPrompt(flag) {
      console.log("hideAssignMemberPrompt called");
      console.log({ flag });
      this.assignMemberPrompt = false;
      if (flag) {
        this.searchForm();
      }
    },
    previewData(tr) {
      console.log("previewData called");
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.previewRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "lgb_dis",
        Action: "report_1",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    getYearCodeOptions() {
      console.log("getYearCodeOptions called");
      // this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
      // var CurrentYearId = this.CurrentYearId;
      var CurrentYearId = this.YearCode;
      console.log("CurrentYearId=" + CurrentYearId);
      this.LoadingFlag = true;
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "JciYearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 3,
        where: [["jci_year_id", ">=", 11]],
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getAreaCodeOptions() {
      console.log("getAreaCodeOptions called");
      var yearCode = this.CurrentYearId;
      console.log("yearCode=" + yearCode);
      if (yearCode != "") {
        this.LoadingFlag = true;
        this.AreaCodeOptionsLoading = true;
        var selectbox1_source = "AreaCode";
        var selectbox1_destination = "AreaCodeOptions";
        var selectbox1_url = "api/year-wise-lom/area-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "Year should not be empty";
        this.toast("error", message);
      }
    },
    getZoneCodeOptions() {
      console.log("getZoneCodeOptions called");
      var yearCode = this.CurrentYearId;
      var areaCode = this.AreaCode;
      console.log("yearCode=" + yearCode + ", areaCode=" + areaCode);
      if (yearCode != "") {
        this.LoadingFlag = true;
        this.ZoneCodeOptionsLoading = true;
        var selectbox1_source = "ZoneCode";
        var selectbox1_destination = "ZoneCodeOptions";
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: yearCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (yearCode == "") {
          message += "Year should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getRegionCodeOptions() {
      console.log("getRegionCodeOptions called");
      var YearCode = this.CurrentYearId;
      var AreaCode = this.AreaCode;
      var ZoneCode = this.ZoneCode;
      console.log(
        "YearCode=" +
        YearCode +
        ", AreaCode=" +
        AreaCode +
        ", ZoneCode=" +
        ZoneCode
      );
      if (YearCode != "" && AreaCode != "" && ZoneCode != "") {
        this.LoadingFlag = true;
        this.RegionCodeOptionsLoading = true;
        var selectbox1_source = "RegionCode";
        var selectbox1_destination = "RegionCodeOptions";
        var selectbox1_url = "api/year-wise-lom/region-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: YearCode,
          AreaCode: AreaCode,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (AreaCode == "") {
          message += "Area should not be empty. ";
        }
        if (ZoneCode == "") {
          message += "Zone should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getLomCodeOptions() {
      console.log("getLomCodeOptions called");
      var YearCode = this.CurrentYearId;
      var ZoneCode = this.ZoneCode;
      var RegionCode = this.RegionCode;
      console.log(
        "YearCode=" +
        YearCode +
        ", ZoneCode=" +
        ZoneCode +
        ", RegionCode=" +
        RegionCode
      );
      if (YearCode != "" && ZoneCode != "") {
        this.LoadingFlag = true;
        this.LomCodeOptionsLoading = true;
        var selectbox1_source = "LomCode";
        var selectbox1_destination = "LomCodeOptions";
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 3,
          YearCode: YearCode,
          ZoneCode: ZoneCode,
          AllOption: 1,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "";
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (ZoneCode == "") {
          message += "Zone should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    getDesignationCodeOptions() {
      console.log("DesignationCodeOptions called");
      this.LoadingFlag = true;
      this.DesignationCodeOptionsLoading = true;
      var selectbox1_source = "DesignationCode";
      var selectbox1_destination = "DesignationCodeOptions";
      var selectbox1_url = "api/designation/options";
      var selectbox1_conditions_array = {
        UserInterface: 5,
        DesignationTypeId: [527, 528],
        ExceptDesignationId: [1, 5, 6, 7],
        AllOption: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    searchForm() {
      console.log("searchForm is called");

      var validate1 = this.$refs.form2.validate();
      var SearchId = this.SearchId;
      var ApprovalStatus = this.ApprovalStatus;
      var MigrationStatus = this.MigrationStatus;
      var UserInterface = SearchId == 1 ? 1 : 3;
      console.log(
        "validate1=" +
        validate1 +
        ", SearchId=" +
        SearchId +
        ", UserInterface=" +
        UserInterface
      );

      if (validate1) {
        this.SearchFlag = true;
        this.tableData1 = [];
        this.DownloadFlag = false;
        this.ExcelFileName = "";
        this.Modules = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/reports/directory-information/lists";
        var upload = {
          UserInterface: 1,
          YearCode: this.JciYearCode,
          ZoneCode: this.ZoneCode,
          LomCode: this.LomCode,
          DesignationCode: this.DesignationCode,
          ApprovalStatus: ApprovalStatus != "" ? ApprovalStatus : null,
          MigrationStatus: MigrationStatus >= 0 ? MigrationStatus : null,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var table = {};
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SearchFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            table = response.data.TableContent;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            var options = table.TableOptions;

            thisIns.tableOptions1 = options;
            thisIns.ExcelDownloadFlag = options.ExcelDownloadButtonFlag;
            thisIns.Modules = options.Modules;

            if (flag == 1) {
              thisIns.tableColumns1 = table.TableHeader;
              thisIns.tableData1 = table.TableData;

              thisIns.DownloadFlag = options.DownloadFlag;
              thisIns.ExcelFileName = options.ExcelFileName;
              thisIns.ExcelFields = options.ExcelFields;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SearchFlag = false;
          });
      } else {
        var message = "Kindly fill the required fields";
        this.toast("error", message);
      }
    },
    regenerateRecords() {
      console.log("regenerateRecords is called");

      var validate1 = true;

      if (validate1) {
        this.SubmitFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-designation/store";
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        var upload = {
          UserInterface: 2,
        };
        console.log("upload=" + JSON.stringify(upload));

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.SubmitFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            this.SubmitFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (LomId == "") {
          message += "Your session is expired. Kindly login again. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    generateReportExcel() {
      console.log("generateReportExcel is called");

      // var validate1 = this.$refs.form2.validate();
      var FromDate = "2021-08-04";
      var ToDate = "2021-08-05";
      var validate1 = true;
      console.log(
        "validate1=" +
        validate1 +
        ", FromDate=" +
        FromDate +
        ", ToDate=" +
        ToDate
      );

      if (validate1) {
        this.ExcelFileName = "";
        this.ExcelLoadingFlag = true;
        this.ExcelFileFlag = false;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url =
          server_url + "api/reports/directory-information/generate-excel";
        var upload = {
          UserInterface: 1,
          YearCode: this.JciYearCode,
          ZoneCode: this.ZoneCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()

            thisIns.ExcelLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.ExcelFileName = records.ExcelFileName;
              thisIns.ExcelDownloadFlag = records.ExcelDownloadFlag;
              thisIns.ExcelFileFlag = true;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.ExcelLoadingFlag = false;
          });
      } else {
        var message = "Kindly fill the required fields";
        this.toast("error", message);
      }
    },
    deleteAlert(tr) {
      console.log("deleteAlert called");
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt +=
          "<br/>Member<b> " + tr.MembershipId + " - " + tr.MemberName + "</b>";
        htmlTxt += "<br/>Designaton<b> " + tr.Designaton + "</b>";
        htmlTxt += "<br/>Zone Name<b> " + tr.Zone + "</b>";
        htmlTxt += "<br/>LO Name<b> " + tr.LO + "</b>";
        htmlTxt += "<br/><br/>Do you want to delete the data?";

        Swal.fire({
          title: "Are you sure?",
          text: "Do you want to delete the data?",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          if (result.isConfirmed) {
            this.deleteRecord(tr);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "Kindly select one record to delete";
        this.sweetAlert("error", message, false);
      }
    },
    deleteRecord(tr) {
      // add todo in store and clear all fields in dialog
      console.log("deleteRecord called");

      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });

      if (n1 > 0) {
        this.LoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-designation/member-delete";
        var upload = {
          UserInterface: 3,
          OrganizationType: 3,
          Year: this.JciYearCode,
          Zone: this.ZoneCode,
          Lom: tr.LOMId,
          Member: tr.MemberId,
          Designation: tr.DesignatonId,
          YearwiseOfficesHeld: tr.YearwiseOfficeHeldId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, false);
              thisIns.searchForm();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        console.log("some errors occured");
        var message = "Kindly select one member to delete";
        this.sweetAlert("error", message, false);
      }
    },
    updateUserRole(item) {
      console.log("updateUserRole is called");
      console.log("item=" + JSON.stringify(item));

      var message = "";
      var tr = item;
      console.log({ tr });

      var MemberId = tr.MemberId || "";
      console.log({ MemberId });

      if (MemberId > 0) {
        Swal.fire({
          title: "Are you sure?",
          text: "This will update the user role and offices held status.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#28a745",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, update it!",
          cancelButtonText: "Cancel",
        }).then((result) => {
          if (result.isConfirmed) {
            var server_url = companyConfig.apiURL;
            var token = this.$session.get("token");
            token = token == (null || undefined) ? 0 : token;
            var add_url = server_url + "api/year-wise-designation/update-user-role";
            var upload = {
              MemberId: MemberId,
            };
            console.log("upload=" + JSON.stringify(upload));
            console.log(
              "server_url=" +
                server_url +
                ", token=" +
                token +
                ", add_url=" +
                add_url
            );

            const thisIns = this;
            var output = "";
            var flag = 0;
            this.ResultFlag = false;
            this.SearchFlag = true;

            this.$http({
              url: add_url,
              method: "POST",
              data: upload,
              headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
              },
              onUploadProgress: function (progressEvent) {
                this.uploadPercentage = parseInt(
                  Math.round((progressEvent.loaded / progressEvent.total) * 100)
                );
              }.bind(this),
            })
              .then(function (response) {
                console.log("response=" + JSON.stringify(response));

                thisIns.ResultFlag = true;
                thisIns.SearchFlag = false;

                output = response.data.output;
                flag = response.data.flag;
                console.log("output=" + output + ", flag=" + flag);

                if (flag == 1) {
                  thisIns.sweetAlert("success", output, true);
                  // thisIns.searchForm();
                } else {
                  thisIns.sweetAlert("error", output, false);
                }
              })
              .catch(function (error) {
                console.log("error=" + error);
                thisIns.SearchFlag = false;
                thisIns.ResultFlag = true;
              });
          }
        });
      } else {
        if (MemberId == null || MemberId == "") {
          message = "Member Details is invalid. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss">
#directory-information-report {
  .v-btn--floating {
    position: relative;
  }

  .v-speed-dial--bottom {
    bottom: 12%;
  }

  .v-speed-dial--right {
    right: 5%;
  }

  .v-data-table {
    th {
      font-weight: bold !important;
      color: #1a237e !important;
      background-color: #f5f5f5 !important;
    }

    td {
      vertical-align: middle;
    }
  }

  .v-card__title {
    word-break: normal;
  }

  .text-danger {
    color: #f44336;
  }

  .v-chip--small {
    height: 24px !important;
  }

  .v-avatar img {
    object-fit: cover;
    border: 1px solid #e0e0e0;
  }

  .v-card {
    transition: all 0.3s;

    &.outlined {
      border: 1px solid rgba(0, 0, 0, 0.12) !important;
    }
  }
}
</style>